// TennisTrack Hub — Form Nuovo Allenamento (Tennis + Fitness) | 029 Project
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import './Allenamenti.css';

const COLPI_TENNIS = [
  'Servizio', 'Dritto', 'Rovescio', 'Volée di dritto', 'Volée di rovescio',
  'Smash', 'Lob', 'Slice', 'Kick', 'Drop shot', 'Risposta', 'Gioco di gambe'
];

const TIPOLOGIE_FITNESS = ['superiore', 'inferiore', 'corpo_intero'];

const FormAllenamento = () => {
  const navigate = useNavigate();
  const { atletaId } = useParams();
  const location = useLocation();
  const tipoDefault = new URLSearchParams(location.search).get('tipo') || 'tennis';

  const [atleta, setAtleta] = useState(null);
  const [tipo, setTipo] = useState(tipoDefault);
  const [form, setForm] = useState({
    data: new Date().toISOString().split('T')[0],
    valutazione: 0,
    obiettivi: '',
    note: ''
  });
  const [esercizi, setEsercizi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');

  useEffect(() => {
    api.get(`/atleti/${atletaId}`)
      .then(res => setAtleta(res.data.atleta))
      .catch(() => navigate('/atleti'));
  }, [atletaId, navigate]);

  // ── Gestione esercizi tennis ──────────────────────────────
  const aggiungiEsercizioTennis = () => {
    setEsercizi([...esercizi, { colpo: COLPI_TENNIS[0], difficolta: 5, descrizione: '' }]);
  };

  const aggiungiEsercizioFitness = () => {
    setEsercizi([...esercizi, { nome: '', tipologia: 'corpo_intero', serie: '', ripetizioni: '', caricoKg: '', note: '' }]);
  };

  const aggiornaEsercizio = (index, campo, valore) => {
    const nuovi = [...esercizi];
    nuovi[index] = { ...nuovi[index], [campo]: valore };
    setEsercizi(nuovi);
  };

  const rimuoviEsercizio = (index) => {
    setEsercizi(esercizi.filter((_, i) => i !== index));
  };

  const cambiaTipo = (nuovoTipo) => {
    setTipo(nuovoTipo);
    setEsercizi([]);
  };

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (esercizi.length === 0) return setErrore('Aggiungi almeno un esercizio.');
    if (tipo === 'tennis' && esercizi.some(e => !e.colpo)) return setErrore('Tutti gli esercizi devono avere un colpo.');
    if (tipo === 'fitness' && esercizi.some(e => !e.nome.trim())) return setErrore('Tutti gli esercizi devono avere un nome.');

    setLoading(true);
    setErrore('');
    try {
      const res = await api.post('/allenamenti', {
        atletaId: parseInt(atletaId),
        tipo,
        ...form,
        valutazione: form.valutazione || null,
        esercizi
      });
      navigate(`/allenamenti/${res.data.allenamento.id}`);
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore durante il salvataggio.');
    } finally {
      setLoading(false);
    }
  };

  if (!atleta) return <div className="page-layout"><Navbar /><div className="loading-msg">Caricamento...</div></div>;

  return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">

        <div className="page-header">
          <div>
            <h1>Nuovo Allenamento</h1>
            <p>Atleta: <strong>{atleta.nome} {atleta.cognome}</strong></p>
          </div>
          <button className="btn btn-secondary" onClick={() => navigate(`/atleti/${atletaId}`)}>
            ← Indietro
          </button>
        </div>

        {errore && <div className="alert alert-error">{errore}</div>}

        {/* Selector tipo */}
        <div className="tipo-selector">
          <button
            type="button"
            className={`tipo-btn ${tipo === 'tennis' ? 'active' : ''}`}
            onClick={() => cambiaTipo('tennis')}
          >
            🎾 Allenamento Tennis
          </button>
          <button
            type="button"
            className={`tipo-btn ${tipo === 'fitness' ? 'active' : ''}`}
            onClick={() => cambiaTipo('fitness')}
          >
            💪 Allenamento Fitness
          </button>
        </div>

        <form onSubmit={handleSubmit}>

          {/* Info generali */}
          <div className="card form-section">
            <h3 className="section-title">📋 Info generali</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Data *</label>
                <input
                  type="date"
                  value={form.data}
                  onChange={e => setForm({ ...form, data: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Valutazione sessione</label>
                <div className="stelle-selector">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      type="button"
                      className={`stella ${form.valutazione >= n ? 'attiva' : ''}`}
                      onClick={() => setForm({ ...form, valutazione: form.valutazione === n ? 0 : n })}
                    >★</button>
                  ))}
                  {form.valutazione > 0 && (
                    <span className="stelle-label">{form.valutazione}/5</span>
                  )}
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>Obiettivi della sessione</label>
              <input
                type="text"
                placeholder="es. Migliorare il servizio slice..."
                value={form.obiettivi}
                onChange={e => setForm({ ...form, obiettivi: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Note generali</label>
              <textarea
                rows={2}
                placeholder="Osservazioni generali sulla sessione..."
                value={form.note}
                onChange={e => setForm({ ...form, note: e.target.value })}
              />
            </div>
          </div>

          {/* Esercizi Tennis */}
          {tipo === 'tennis' && (
            <div className="card form-section">
              <div className="section-header-inline">
                <h3 className="section-title">🎾 Esercizi Tennis</h3>
                <button type="button" className="btn btn-secondary btn-sm" onClick={aggiungiEsercizioTennis}>
                  + Aggiungi esercizio
                </button>
              </div>

              {esercizi.length === 0 && (
                <div className="esercizi-vuoti">
                  <p>Nessun esercizio aggiunto. Clicca "Aggiungi esercizio" per iniziare.</p>
                </div>
              )}

              {esercizi.map((es, i) => (
                <div key={i} className="esercizio-card">
                  <div className="esercizio-header">
                    <span className="esercizio-num">#{i + 1}</span>
                    <button type="button" className="btn-rimuovi" onClick={() => rimuoviEsercizio(i)}>✕</button>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Colpo</label>
                      <select
                        value={es.colpo}
                        onChange={e => aggiornaEsercizio(i, 'colpo', e.target.value)}
                      >
                        {COLPI_TENNIS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Difficoltà (1–10)</label>
                      <div className="difficolta-slider">
                        <input
                          type="range"
                          min="1" max="10"
                          value={es.difficolta || 5}
                          onChange={e => aggiornaEsercizio(i, 'difficolta', parseInt(e.target.value))}
                        />
                        <span className="difficolta-val">{es.difficolta || 5}</span>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Descrizione esercizio</label>
                    <textarea
                      rows={2}
                      placeholder="Descrivi l'esercizio, variazioni, punti chiave..."
                      value={es.descrizione}
                      onChange={e => aggiornaEsercizio(i, 'descrizione', e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Esercizi Fitness */}
          {tipo === 'fitness' && (
            <div className="card form-section">
              <div className="section-header-inline">
                <h3 className="section-title">💪 Esercizi Fitness</h3>
                <button type="button" className="btn btn-secondary btn-sm" onClick={aggiungiEsercizioFitness}>
                  + Aggiungi esercizio
                </button>
              </div>

              {esercizi.length === 0 && (
                <div className="esercizi-vuoti">
                  <p>Nessun esercizio aggiunto. Clicca "Aggiungi esercizio" per iniziare.</p>
                </div>
              )}

              {esercizi.map((es, i) => (
                <div key={i} className="esercizio-card">
                  <div className="esercizio-header">
                    <span className="esercizio-num">#{i + 1}</span>
                    <button type="button" className="btn-rimuovi" onClick={() => rimuoviEsercizio(i)}>✕</button>
                  </div>
                  <div className="form-row">
                    <div className="form-group" style={{ flex: 2 }}>
                      <label>Nome esercizio *</label>
                      <input
                        type="text"
                        placeholder="es. Squat, Panca piana, Affondi..."
                        value={es.nome}
                        onChange={e => aggiornaEsercizio(i, 'nome', e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Tipologia</label>
                      <select
                        value={es.tipologia}
                        onChange={e => aggiornaEsercizio(i, 'tipologia', e.target.value)}
                      >
                        <option value="superiore">Superiore</option>
                        <option value="inferiore">Inferiore</option>
                        <option value="corpo_intero">Corpo intero</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row form-row-3">
                    <div className="form-group">
                      <label>Serie</label>
                      <input type="number" min="1" max="20" placeholder="3"
                        value={es.serie}
                        onChange={e => aggiornaEsercizio(i, 'serie', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Ripetizioni</label>
                      <input type="number" min="1" max="100" placeholder="12"
                        value={es.ripetizioni}
                        onChange={e => aggiornaEsercizio(i, 'ripetizioni', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Carico (kg)</label>
                      <input type="number" min="0" max="500" step="0.5" placeholder="60"
                        value={es.caricoKg}
                        onChange={e => aggiornaEsercizio(i, 'caricoKg', e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Note esercizio</label>
                    <input type="text" placeholder="es. Pausa 90s tra le serie..."
                      value={es.note}
                      onChange={e => aggiornaEsercizio(i, 'note', e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Azioni */}
          <div className="form-actions form-actions-bottom">
            <button type="button" className="btn btn-secondary" onClick={() => navigate(`/atleti/${atletaId}`)}>
              Annulla
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Salvataggio...' : '💾 Salva allenamento'}
            </button>
          </div>
        </form>

      </main>
    </div>
  );
};

export default FormAllenamento;
